import { useState, useEffect, useMemo } from 'react';
import { Receipt, Plus, Edit, Trash2, Wallet } from 'lucide-react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { cajaChicaApi } from '../lib/api';
import { fechaLima } from '../lib/fechas';

interface Gasto {
  id: string;
  fecha: string;
  tipo: 'ingreso' | 'gasto' | 'saldo_inicial';
  monto: number;
  descripcion: string;
  categoria?: string;
  caja?: 'mantenimiento' | 'ventas' | 'servicios' | 'chica';
}

const CATEGORIAS = ['Nómina', 'Adelantos', 'Repuestos', 'Servicios', 'Alquiler', 'Combustible', 'Otro'];

type Rango = 'dia' | 'semana' | 'mes';

function hoyStr() {
  return fechaLima(new Date());
}

// Devuelve [desde, hasta] (inclusive, YYYY-MM-DD) segun el rango elegido, anclado a
// una fecha de referencia (por defecto hoy).
function calcularRango(rango: Rango, referencia: string): [string, string] {
  const ref = new Date(referencia + 'T00:00:00');
  if (rango === 'dia') {
    return [referencia, referencia];
  }
  if (rango === 'semana') {
    const dia = ref.getDay();
    const diffLunes = dia === 0 ? 6 : dia - 1;
    const lunes = new Date(ref);
    lunes.setDate(ref.getDate() - diffLunes);
    const domingo = new Date(lunes);
    domingo.setDate(lunes.getDate() + 6);
    return [fechaLima(lunes), fechaLima(domingo)];
  }
  // mes
  const inicio = new Date(ref.getFullYear(), ref.getMonth(), 1);
  const fin = new Date(ref.getFullYear(), ref.getMonth() + 1, 0);
  return [fechaLima(inicio), fechaLima(fin)];
}

const Gastos = () => {
  const navigate = useNavigate();
  const [movimientos, setMovimientos] = useState<Gasto[]>([]);
  const [cargando, setCargando] = useState(true);
  const [rango, setRango] = useState<Rango>('mes');
  const [fechaReferencia, setFechaReferencia] = useState(hoyStr());
  const [mostrarModal, setMostrarModal] = useState(false);
  const [gastoEditando, setGastoEditando] = useState<Gasto | null>(null);
  const [form, setForm] = useState<{ monto: string; descripcion: string; categoria: string; caja: '' | 'mantenimiento' | 'ventas' | 'servicios' | 'chica' }>({ monto: '', descripcion: '', categoria: CATEGORIAS[0], caja: 'ventas' });
  // Fondo de Caja Chica: desde el 07/10/2026 todo gasto nuevo sale de aqui.
  const [fondo, setFondo] = useState<any>(null);

  useEffect(() => { cargarDatos(); }, []);

  const cargarDatos = async () => {
    setCargando(true);
    cajaChicaApi.getSaldo('ventas').then((r) => setFondo({ abierto: true, saldo: Number(r?.saldo) || 0 })).catch(() => setFondo(null));
    try {
      const data = await cajaChicaApi.getAll();
      setMovimientos((data || []).filter((m: Gasto) => m.tipo === 'gasto'));
    } catch (error) {
      console.error('Error cargando gastos:', error);
    } finally {
      setCargando(false);
    }
  };

  const [desde, hasta] = useMemo(() => calcularRango(rango, fechaReferencia), [rango, fechaReferencia]);

  const gastosFiltrados = useMemo(() => {
    return movimientos
      .filter(m => {
        const f = m.fecha.slice(0, 10);
        return f >= desde && f <= hasta;
      })
      .sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime());
  }, [movimientos, desde, hasta]);

  const totalFiltrado = gastosFiltrados.reduce((sum, g) => sum + Number(g.monto || 0), 0);

  const porCategoria = useMemo(() => {
    const mapa: Record<string, number> = {};
    gastosFiltrados.forEach(g => {
      const cat = g.categoria || 'Otro';
      mapa[cat] = (mapa[cat] || 0) + Number(g.monto || 0);
    });
    return Object.entries(mapa).sort((a, b) => b[1] - a[1]);
  }, [gastosFiltrados]);

  const abrirNuevo = () => {
    setGastoEditando(null);
    setForm({ monto: '', descripcion: '', categoria: CATEGORIAS[0], caja: 'ventas' });
    setMostrarModal(true);
  };

  const abrirEdicion = (g: Gasto) => {
    setGastoEditando(g);
    setForm({ monto: String(g.monto), descripcion: g.descripcion, categoria: g.categoria || CATEGORIAS[0], caja: g.caja || '' });
    setMostrarModal(true);
  };

  const guardar = async () => {
    if (!form.monto || !form.descripcion) {
      alert('Completá el monto y la descripción');
      return;
    }
    if (!gastoEditando && fondo?.abierto && parseFloat(form.monto) > fondo.saldo &&
        !confirm(`Este gasto (S/ ${parseFloat(form.monto).toFixed(2)}) supera el saldo de Caja Repuestos (S/ ${Number(fondo.saldo).toFixed(2)}). ¿Registrarlo igual? La caja quedará en negativo.`)) {
      return;
    }
    try {
      const datos = { tipo: 'gasto', monto: parseFloat(form.monto), descripcion: form.descripcion, categoria: form.categoria, caja: gastoEditando ? (form.caja || undefined) : 'ventas' };
      if (gastoEditando) {
        await cajaChicaApi.update(gastoEditando.id, datos);
      } else {
        await cajaChicaApi.create(datos);
      }
      setMostrarModal(false);
      cargarDatos();
    } catch (error) {
      console.error('Error guardando gasto:', error);
      alert('Error al guardar el gasto');
    }
  };

  const eliminar = async (id: string) => {
    if (!confirm('¿Eliminar este gasto? Queda registrado en Auditoría.')) return;
    await cajaChicaApi.delete(id);
    cargarDatos();
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-2">
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Receipt className="text-red-600" size={32} />
            Gastos
          </h1>
          <button
            onClick={() => navigate('/caja-repuestos')}
            className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm font-medium"
          >
            <Wallet size={16} className="text-blue-600" />
            Ver Cajas
          </button>
        </div>
        <p className="text-sm text-gray-500 mb-6">
          Todo egreso de dinero se registra acá. Desde el 10/10/2026 los gastos nuevos salen de la <b>Caja Repuestos</b> (que unió Caja Ventas y Caja Chica); las compras de repuestos se registran en <b>Compras de repuestos</b>.
        </p>
        {fondo?.abierto && (
          <div onClick={() => navigate('/caja-repuestos')} className={`cursor-pointer rounded-lg p-4 mb-6 flex flex-wrap items-center gap-x-8 gap-y-2 border ${fondo.saldo < 0 ? 'bg-red-50 border-red-300' : 'bg-purple-50 border-purple-200'}`}>
            <div><div className="text-xs text-gray-500 uppercase font-semibold">Caja Repuestos · saldo disponible</div>
              <div className={`text-2xl font-bold ${fondo.saldo < 0 ? 'text-red-600' : 'text-purple-700'}`}>S/ {Number(fondo.saldo).toFixed(2)}</div></div>
            <div className="text-xs text-purple-700 ml-auto">Ver Caja Repuestos →</div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow p-4 mb-6 flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Ver por</label>
            <div className="flex gap-1">
              {(['dia', 'semana', 'mes'] as Rango[]).map(r => (
                <button
                  key={r}
                  onClick={() => setRango(r)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium ${rango === r ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  {r === 'dia' ? 'Día' : r === 'semana' ? 'Semana' : 'Mes'}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Fecha de referencia</label>
            <input type="date" value={fechaReferencia} onChange={(e) => setFechaReferencia(e.target.value)} className="border rounded-lg px-3 py-2" />
          </div>
          <span className="text-sm text-gray-500">
            {desde === hasta ? desde : `${desde} → ${hasta}`} ({gastosFiltrados.length} gastos)
          </span>
          <button
            onClick={abrirNuevo}
            className="ml-auto bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-lg flex items-center gap-2"
          >
            <Plus size={18} />
            Nuevo Gasto
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-red-500">
            <p className="text-sm text-gray-600">Total gastado en el período</p>
            <p className="text-3xl font-bold text-red-600">S/ {totalFiltrado.toFixed(2)}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-600 mb-2">Por categoría</p>
            {porCategoria.length === 0 ? (
              <p className="text-xs text-gray-400">Sin gastos en este período</p>
            ) : (
              <div className="space-y-1">
                {porCategoria.map(([cat, monto]) => (
                  <div key={cat} className="flex justify-between text-sm">
                    <span className="text-gray-600">{cat}</span>
                    <span className="font-semibold">S/ {monto.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          {cargando ? (
            <p className="text-center py-8 text-gray-500">Cargando...</p>
          ) : gastosFiltrados.length === 0 ? (
            <p className="text-center py-8 text-gray-500">No hay gastos en este período</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Categoría</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Caja</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descripción</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Monto</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {gastosFiltrados.map((g) => (
                    <tr key={g.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">{format(new Date(g.fecha), 'dd/MM/yyyy HH:mm')}</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
                          {g.categoria || 'Otro'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${g.caja === 'chica' ? 'bg-purple-100 text-purple-700' : g.caja === 'mantenimiento' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}>
                          {g.caja === 'chica' ? 'Caja Chica (hasta 09/10)' : g.caja === 'mantenimiento' ? 'Mantenimiento' : g.caja === 'ventas' ? 'Caja Repuestos' : g.caja === 'servicios' ? 'Servicios' : 'Sin definir'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">{g.descripcion}</td>
                      <td className="px-4 py-3 text-right text-sm font-semibold text-red-600">- S/ {Number(g.monto).toFixed(2)}</td>
                      <td className="px-4 py-3 text-center">
                        {(g.caja === 'chica' || g.caja === 'ventas') && g.categoria === 'Repuestos' && (g.descripcion || '').startsWith('Compra de repuestos') ? (
                          <button onClick={() => navigate('/compras-repuestos')} className="text-xs text-purple-700 underline">Ver en Compras de repuestos</button>
                        ) : (<>
                        <button onClick={() => abrirEdicion(g)} className="text-blue-600 hover:text-blue-800 mr-3">
                          <Edit size={18} />
                        </button>
                        <button onClick={() => eliminar(g.id)} className="text-red-600 hover:text-red-800">
                          <Trash2 size={18} />
                        </button>
                        </>)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {mostrarModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-2xl font-bold mb-4">{gastoEditando ? 'Editar Gasto' : 'Nuevo Gasto'}</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Categoría</label>
                <select value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })} className="w-full px-4 py-2 border rounded-lg">
                  {CATEGORIAS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              {!gastoEditando && (
                <div className="rounded-lg bg-purple-50 border border-purple-200 px-3 py-2 text-sm text-purple-800">
                  Este gasto sale de la <b>Caja Repuestos</b>{fondo?.abierto ? <> · saldo disponible <b>S/ {Number(fondo.saldo).toFixed(2)}</b></> : null}.
                  {form.categoria === 'Repuestos' && <div className="text-xs mt-1">Si es una compra de repuestos, mejor regístrala en <b>Compras de repuestos</b> para llevar su balance aparte.</div>}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium mb-2">Monto (S/)</label>
                <input
                  type="number"
                  step="0.01"
                  value={form.monto}
                  onChange={(e) => setForm({ ...form, monto: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Descripción</label>
                <textarea
                  value={form.descripcion}
                  onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                  rows={3}
                  placeholder="Ej: Pago de nómina quincena 1-15 agosto"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={() => setMostrarModal(false)} className="flex-1 px-4 py-2 bg-gray-200 rounded-lg">
                Cancelar
              </button>
              <button onClick={guardar} className="flex-1 px-4 py-2 text-white rounded-lg bg-red-600 hover:bg-red-700">
                {gastoEditando ? 'Actualizar' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gastos;
